<!-- @format -->

<div
	[ngClass]="{ 'custom-section': true, card: true }"
	*ngIf="!(overviewService.showOverview$ | async)">
	<div class="home-tabs">
		<button
			type="button"
			[class.active]="activeTab === 'workflows'"
			(click)="setActiveTab('workflows')">
			Workflows
		</button>
		<button
			type="button"
			[class.active]="activeTab === 'forms'"
			(click)="setActiveTab('forms')">
			Forms
		</button>
	</div>

	<div *ngIf="activeTab === 'workflows'" class="workflow-tab">
		<div class="workflow-heading">
			<div>
				<h1>Workflows</h1>
			</div>
		</div>

		<div *ngIf="workflowsLoading" class="workflow-muted">Loading workflows...</div>
		<div *ngIf="workflowError" class="workflow-error">{{ workflowError }}</div>

		<!-- Super Admin: browse all workflows by account holder + client -->
		<div class="wf-admin-browse" *ngIf="currentRole === roles.superAdmin && !showHistory">
			<div class="dropdown-group">
				<app-dropdown
					[disabled]="false"
					[options]="companies"
					(onSelectOption)="onWfCompanySelect($event)"
					[fields]="['companyId', 'companyName']"
					[label]="wfSelectedCompany.companyName?.length ? wfSelectedCompany.companyName : 'Choose Account Holder'"></app-dropdown>
				<app-dropdown
					[disabled]="!wfSelectedCompany.companyId"
					[options]="wfPlants"
					(onSelectOption)="onWfPlantSelect($event)"
					[fields]="['plantId', 'plantName']"
					[label]="wfSelectedPlant.plantName?.length ? wfSelectedPlant.plantName : 'Choose Client'"></app-dropdown>
			</div>

			<section class="workflow-group" *ngIf="wfSelectedPlant.plantId">
				<div class="workflow-section-heading">
					<h2>All Workflows</h2>
				</div>
				<div *ngIf="adminWorkflowsLoading" class="workflow-muted">Loading workflows...</div>
				<div *ngIf="adminWorkflowsError" class="workflow-error">{{ adminWorkflowsError }}</div>
				<div *ngIf="!adminWorkflowsLoading && !adminWorkflowsError && adminWorkflows.length === 0" class="workflow-muted">
					No workflows for this client.
				</div>
				<div class="workflow-list" *ngIf="adminWorkflows.length > 0">
					<article class="workflow-card" *ngFor="let item of pagedAdminWorkflows" role="link" tabindex="0"
						[attr.data-status]="statusToken(item)"
						[attr.data-state]="isTerminalWorkflowItem(item) ? 'terminal' : null"
						[attr.aria-label]="cardAriaLabel(item)"
						(click)="openWorkflowItem(item, true)"
						(keydown.enter)="openWorkflowItem(item, true)"
						(keydown.space)="$event.preventDefault(); openWorkflowItem(item, true)">
						<div class="wf-card-top">
							<div class="wf-card-head">
								<span class="wf-type" *ngIf="item.summaryTitle">{{ item.name }}</span>
								<span class="wf-status">{{ pillLabel(item) }}</span>
							</div>
							<h3 class="wf-subject" [title]="item.summaryTitle || item.name">{{ item.summaryTitle || item.name }}</h3>
						</div>

						<div class="wf-stage" *ngIf="item.stageCount">
							<div class="wf-track" role="img" [attr.aria-label]="stageProgressLabel(item)">
								<span class="wf-seg" *ngFor="let seg of stageSegments(item)"
									[class.is-done]="seg === 'done'" [class.is-current]="seg === 'current'"></span>
							</div>
							<div class="wf-stage-row">
								<span class="wf-stage-label">{{ stageProgressLabel(item) }}</span>
								<!-- RT-21: overdue rides the stage row — the SLA belongs to the current stage. -->
								<span class="wf-flag" *ngIf="overdueLabel(item)" title="This stage has passed its time budget">
									<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M8 2.5 1.8 13.3h12.4L8 2.5Z"/><path d="M8 6.6v3.1"/><path d="M8 11.6h.01"/></svg>
									{{ overdueLabel(item) }}
								</span>
							</div>
						</div>

						<dl class="wf-facts">
							<div><dt>Submitted</dt><dd>{{ formatShort(item.startedAt) }}</dd></div>
							<div><dt>Updated</dt><dd>{{ relativeTime(item.updatedAt) }}</dd></div>
							<div *ngIf="item.initiatorName"><dt>Initiator</dt><dd [title]="item.initiatorName">{{ item.initiatorName }}</dd></div>
						</dl>

						<div class="wf-card-foot">
							<span class="wf-ref" title="Reference">WF-{{ item.workflowItemId }}</span>
							<button type="button" class="wf-action" (click)="$event.stopPropagation(); openWorkflowItem(item, true)">
								{{ rowActionLabel(item) }}
							</button>
						</div>
					</article>
				</div>
				<div class="wf-pagination" *ngIf="adminWorkflows.length > 0">
					<button type="button" class="wf-page-arrow" [disabled]="adminWfPage === 1" (click)="setAdminWfPage(adminWfPage - 1)" aria-label="Previous page">‹</button>
					<button type="button" class="wf-page-num" *ngFor="let p of cardPageList(adminWorkflows.length)" [class.active]="p === adminWfPage" (click)="setAdminWfPage(p)">{{ p }}</button>
					<button type="button" class="wf-page-arrow" [disabled]="adminWfPage === totalCardPages(adminWorkflows.length)" (click)="setAdminWfPage(adminWfPage + 1)" aria-label="Next page">›</button>
				</div>
			</section>
		</div>

		<div *ngIf="!workflowsLoading && !workflowError && currentRole !== roles.superAdmin">
			<div class="wf-filter-tabs">
				<button type="button" class="wf-filter-tab" [class.active]="workflowFilter === 'tasks'" (click)="setWorkflowFilter('tasks')">
					<svg class="wf-tab-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"/><rect x="9" y="3" width="6" height="4" rx="1"/><path d="m9 14 2 2 4-4"/></svg>
					My Tasks
				</button>
				<button type="button" class="wf-filter-tab" [class.active]="workflowFilter === 'start'" (click)="setWorkflowFilter('start')">
					<svg class="wf-tab-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 8v8M8 12h8"/></svg>
					New workflow
				</button>
				<button type="button" class="wf-filter-tab" [class.active]="workflowFilter === 'history'" (click)="setWorkflowFilter('history')">
					<svg class="wf-tab-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 3v5h5"/><path d="M3.05 13A9 9 0 1 0 6 5.3L3 8"/><path d="M12 7v5l3 2"/></svg>
					View History
				</button>
			</div>

			<!-- My Tasks -->
			<section class="workflow-group wf-group-flush" *ngIf="workflowFilter === 'tasks'">
				<div *ngIf="needsMyAction.length === 0" class="workflow-muted">No workflows need your action.</div>
				<div class="workflow-list" *ngIf="needsMyAction.length > 0">
					<article class="workflow-card" *ngFor="let item of pagedNeedsMyAction" role="link" tabindex="0"
						[attr.data-status]="statusToken(item)"
						[attr.data-state]="isTerminalWorkflowItem(item) ? 'terminal' : null"
						[attr.aria-label]="cardAriaLabel(item)"
						(click)="openWorkflowItem(item)"
						(keydown.enter)="openWorkflowItem(item)"
						(keydown.space)="$event.preventDefault(); openWorkflowItem(item)">
						<div class="wf-card-top">
							<div class="wf-card-head">
								<span class="wf-type" *ngIf="item.summaryTitle">{{ item.name }}</span>
								<span class="wf-status">{{ pillLabel(item) }}</span>
							</div>
							<h3 class="wf-subject" [title]="item.summaryTitle || item.name">{{ item.summaryTitle || item.name }}</h3>
						</div>

						<div class="wf-stage" *ngIf="item.stageCount">
							<div class="wf-track" role="img" [attr.aria-label]="stageProgressLabel(item)">
								<span class="wf-seg" *ngFor="let seg of stageSegments(item)"
									[class.is-done]="seg === 'done'" [class.is-current]="seg === 'current'"></span>
							</div>
							<div class="wf-stage-row">
								<span class="wf-stage-label">{{ stageProgressLabel(item) }}</span>
								<!-- RT-21: overdue rides the stage row — the SLA belongs to the current stage. -->
								<span class="wf-flag" *ngIf="overdueLabel(item)" title="This stage has passed its time budget">
									<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M8 2.5 1.8 13.3h12.4L8 2.5Z"/><path d="M8 6.6v3.1"/><path d="M8 11.6h.01"/></svg>
									{{ overdueLabel(item) }}
								</span>
							</div>
						</div>

						<dl class="wf-facts">
							<div><dt>Submitted</dt><dd>{{ formatShort(item.startedAt) }}</dd></div>
							<div><dt>Updated</dt><dd>{{ relativeTime(item.updatedAt) }}</dd></div>
							<div *ngIf="item.initiatorName"><dt>Initiator</dt><dd [title]="item.initiatorName">{{ item.initiatorName }}</dd></div>
							<div *ngFor="let f of summaryMetaFields(item)"><dt>{{ f.label }}</dt><dd [title]="f.value">{{ f.value }}</dd></div>
						</dl>

						<div class="wf-card-foot">
							<span class="wf-ref" title="Reference">WF-{{ item.workflowItemId }}</span>
							<button type="button" class="wf-action is-primary" (click)="$event.stopPropagation(); openWorkflowItem(item)">
								{{ rowActionLabel(item) }}
							</button>
						</div>
					</article>
				</div>
			</section>

			<!-- Start New -->
			<section class="workflow-group wf-group-flush" *ngIf="workflowFilter === 'start'">
				<div *ngIf="availableToStart.length === 0" class="workflow-muted">No workflows available to start.</div>
				<div class="workflow-list" *ngIf="availableToStart.length > 0">
					<article class="workflow-card" *ngFor="let workflow of pagedAvailableToStart" role="link" tabindex="0"
						[attr.data-status]="workflow.hasDraft ? 'draft' : 'progress'"
						[attr.aria-label]="workflow.name + ', ' + (workflow.hasDraft ? 'Draft saved' : 'Available to start')"
						(click)="openWorkflow(workflow)"
						(keydown.enter)="openWorkflow(workflow)"
						(keydown.space)="$event.preventDefault(); openWorkflow(workflow)">
						<div class="wf-card-top">
							<div class="wf-card-head">
								<span class="wf-type">Workflow template</span>
								<span class="wf-status">{{ workflow.hasDraft ? 'Draft saved' : 'Available' }}</span>
							</div>
							<h3 class="wf-subject" [title]="workflow.name">{{ workflow.name }}</h3>
						</div>

						<p class="wf-desc" *ngIf="workflow.description">{{ workflow.description }}</p>

						<div class="wf-flow" *ngIf="stageFlow(workflow.stageCount).length">
							<div class="wf-flow-steps">
								<ng-container *ngFor="let step of stageFlow(workflow.stageCount); let last = last; let i = index">
									<span class="wf-flow-step" [class.wf-flow-start]="i === 0">{{ step }}</span>
									<span class="wf-flow-arrow" *ngIf="!last">→</span>
								</ng-container>
							</div>
						</div>

						<dl class="wf-facts">
							<div><dt>Version</dt><dd>{{ workflow.version }}</dd></div>
							<div><dt>Stages</dt><dd>{{ workflow.stageCount }}</dd></div>
						</dl>

						<div class="wf-card-foot wf-foot-plain">
							<button type="button" class="wf-action is-primary" (click)="$event.stopPropagation(); openWorkflow(workflow)">
								{{ workflow.actionLabel || 'Start' }}
							</button>
						</div>
					</article>
				</div>
			</section>

			<!-- History -->
			<div class="workflow-history wf-group-flush" *ngIf="workflowFilter === 'history'">
				<div class="wf-history-toolbar" *ngIf="!historyLoading && !historyError && historyItems.length > 0">
					<div class="wf-history-search">
						<img src="assets/search.svg" alt="" />
						<input type="text" placeholder="Search history" [value]="historySearch" (input)="onHistorySearch(hsearch.value)" #hsearch />
					</div>
					<div class="wf-history-filter-wrap">
						<button type="button" class="wf-history-filter-btn" [class.active]="historyStatusFilter !== 'all'" (click)="toggleHistoryFilter()" title="Filter by status">
							<img src="assets/filter.svg" alt="Filter" />
							<span class="wf-history-filter-dot" *ngIf="historyStatusFilter !== 'all'"></span>
						</button>
						<div class="wf-history-filter-menu" *ngIf="historyFilterOpen">
							<button type="button" class="wf-history-filter-option" *ngFor="let opt of historyStatusOptions"
								[class.selected]="historyStatusFilter === opt.value" (click)="setHistoryStatusFilter(opt.value)">
								{{ opt.label }}
							</button>
						</div>
					</div>
					<!-- #3: toggle submission order (newest ↔ oldest) -->
					<button type="button" class="wf-history-sort-btn" (click)="toggleWorkflowSort()"
						[title]="workflowSort === 'newest' ? 'Showing newest first — click for oldest first' : 'Showing oldest first — click for newest first'">
						<img src="assets/sort.svg" alt="Sort" onerror="this.style.display='none'" />
						<span>{{ workflowSort === 'newest' ? 'Newest first' : 'Oldest first' }}</span>
					</button>
				</div>
				<div class="wf-history-filter-backdrop" *ngIf="historyFilterOpen" (click)="toggleHistoryFilter()"></div>
				<div *ngIf="historyLoading" class="workflow-muted">Loading workflow history...</div>
				<div *ngIf="historyError" class="workflow-error">{{ historyError }}</div>
				<div *ngIf="!historyLoading && !historyError && historyItems.length === 0" class="workflow-muted">
					No workflow history yet.
				</div>
				<div *ngIf="!historyLoading && !historyError && historyItems.length > 0 && pagedHistoryItems.length === 0" class="workflow-muted">
					No workflow history matches your search.
				</div>
				<div class="workflow-list" *ngIf="pagedHistoryItems.length > 0">
						<article class="workflow-card" *ngFor="let item of pagedHistoryItems" role="link" tabindex="0"
							[attr.data-status]="statusToken(item)"
							[attr.data-state]="isTerminalWorkflowItem(item) ? 'terminal' : null"
							[attr.aria-label]="cardAriaLabel(item)"
							(click)="openWorkflowItem(item, true)"
							(keydown.enter)="openWorkflowItem(item, true)"
							(keydown.space)="$event.preventDefault(); openWorkflowItem(item, true)">
							<div class="wf-card-top">
								<div class="wf-card-head">
									<span class="wf-type" *ngIf="item.summaryTitle">{{ item.name }}</span>
									<span class="wf-status">{{ pillLabel(item) }}</span>
								</div>
								<h3 class="wf-subject" [title]="item.summaryTitle || item.name">{{ item.summaryTitle || item.name }}</h3>
							</div>

							<div class="wf-stage" *ngIf="item.stageCount">
								<div class="wf-track" role="img" [attr.aria-label]="stageProgressLabel(item)">
									<span class="wf-seg" *ngFor="let seg of stageSegments(item)"
										[class.is-done]="seg === 'done'" [class.is-current]="seg === 'current'"></span>
								</div>
								<div class="wf-stage-row">
									<span class="wf-stage-label">{{ stageProgressLabel(item) }}</span>
								</div>
							</div>

							<dl class="wf-facts">
								<div><dt>Submitted</dt><dd>{{ formatShort(item.startedAt) }}</dd></div>
								<div><dt>Updated</dt><dd>{{ relativeTime(item.updatedAt) }}</dd></div>
								<div *ngIf="item.initiatorName"><dt>Initiator</dt><dd [title]="item.initiatorName">{{ item.initiatorName }}</dd></div>
							</dl>

							<div class="wf-card-foot">
								<span class="wf-ref" title="Reference">WF-{{ item.workflowItemId }}</span>
								<button type="button" class="wf-action" (click)="$event.stopPropagation(); openWorkflowItem(item, true)">
									{{ rowActionLabel(item) }}
								</button>
							</div>
						</article>
					</div>
			</div>
			<div class="wf-pagination" *ngIf="activeWorkflowCount > 0">
				<button type="button" class="wf-page-arrow" [disabled]="workflowPage === 1" (click)="setWorkflowPage(workflowPage - 1)" aria-label="Previous page">‹</button>
				<button type="button" class="wf-page-num" *ngFor="let p of cardPageList(activeWorkflowCount)" [class.active]="p === workflowPage" (click)="setWorkflowPage(p)">{{ p }}</button>
				<button type="button" class="wf-page-arrow" [disabled]="workflowPage === totalCardPages(activeWorkflowCount)" (click)="setWorkflowPage(workflowPage + 1)" aria-label="Next page">›</button>
			</div>
		</div>

	</div>

	<div *ngIf="activeTab === 'forms'">
	<!-- DROPDOWNS -->
	<div
		[ngClass]="{
			plant: currentRole !== roles.superAdmin,
			'dropdown-group': true
		}">
		<app-dropdown
			*ngIf="currentRole === roles.superAdmin"
			[disabled]="false"
			[options]="companies"
			selectedItem="{{ selectedValues.company }}"
			(onSelectOption)="retrievePlantsByCompanyId($event)"
			[fields]="['companyId', 'companyName']"
			[label]="
				selectedValues.company.companyName.length === 0
					? 'Choose Account Holder'
					: selectedValues.company.companyName
			"></app-dropdown>
		<app-dropdown
			[disabled]="
				currentRole === roles.superAdmin &&
				selectedValues.company.companyId === 0
					? true
					: false
			"
			[options]="plants"
			selectedItem="{{ selectedValues.plant }}"
			(onSelectOption)="retrieveUnitsByPlantId($event)"
			[fields]="['plantId', 'plantName']"
			[label]="
				selectedValues.plant.plantName === ''
					? 'Choose Client'
					: selectedValues.plant.plantName
			">
		</app-dropdown>
	</div>
	<div class="unit-container">
			<a
				[routerLink]="['/home', 'overview', 'branch']"
				[queryParams]="{ unitId: unit.unitId }"
				[state]="[unit, selectedValues]"
			class="unit-item"
			*ngFor="let unit of units"
			(click)="toggleOverview()">
			<div>
				<img
					src="../../../assets/unit-dark.svg"
					alt="icon" />
			</div>
			<div class="unit-name">{{ unit.unitName }}</div>
			<div class="logs">{{ unit?.logsheets?.length }} Forms</div>
		</a>
	</div>
	<div
		[ngClass]="{
			'pagination-location': units.length < 4,
			pagination: true
		}"
		*ngIf="units.length !== 0">
		<button
			[style.opacity]="currentPage === 1 ? 0.5 : 1"
			type="button"
			(click)="performDecrement()">
			<img
				class="arrow flipped"
				src="../../assets/triangle-icon.svg"
				alt="navigate back" />
		</button>
		<div *ngFor="let item of loopArray; let i = index">
			<button
				*ngIf="
					item <= totalPages &&
					currentPage > 2 &&
					currentPage < totalPages - 1
						? item >= currentPage - 2 && item <= currentPage + 2
						: currentPage === 1
						? item >= currentPage && item <= currentPage + 4
						: currentPage === 2
						? item >= currentPage - 1 && item <= currentPage + 3
						: currentPage === totalPages - 1
						? item >= currentPage - 3 && item <= currentPage + 1
						: currentPage === totalPages
						? item >= currentPage - 4 && item <= currentPage
						: true
				"
				[style]="currentPage === item ? selectedPage : ''"
				(click)="setCurrentPage(item)"
				class="page-number">
				{{ item }}
			</button>
		</div>
		<button
			[style.opacity]="currentPage === totalPages ? 0.5 : 1"
			type="button"
			(click)="performIncrement()">
			<img
				class="arrow"
				src="../../assets/triangle-icon.svg"
				alt="navigate forward" />
		</button>
	</div>

	<div
		class="center"
		*ngIf="
			selectedValues?.company?.companyName.length === 0 &&
			currentRole === roles.superAdmin
		">
		<app-empty-state [data]="emptyStateCompanies" />
	</div>
	<div
		class="center"
		*ngIf="showPlantEmptyState()">
		<app-empty-state [data]="emptyStatePlants" />
	</div>
	<div
		class="center"
		*ngIf="selectedValues?.plant?.plantId > 0 && units.length === 0">
		<app-empty-state [data]="emptyStateUnits" />
	</div>
	</div>
</div>
<div *ngIf="overviewService.showOverview$ | async">
	<router-outlet></router-outlet>
</div>
